import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, Activity, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { branding } from '@/config/branding';
import { useAuth } from '../AuthProvider';

type Phase = 'request' | 'confirm';

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { requestPasswordReset, confirmPasswordReset, error, clearError, isLoading } = useAuth();

  const [phase, setPhase] = useState<Phase>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [successNote, setSuccessNote] = useState('');

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!email) {
      setLocalError('Please enter your email');
      return;
    }

    const result = await requestPasswordReset(email);
    if (result.kind === 'code-sent') {
      setSuccessNote(`A verification code was sent to ${email}.`);
      setPhase('confirm');
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!code || !newPassword) {
      setLocalError('Please enter the code and new password');
      return;
    }
    if (newPassword.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    const result = await confirmPasswordReset(email, code, newPassword);
    if (result.kind === 'success') {
      navigate('/login?reset=ok');
    }
  };

  const errorBanner = (error || localError) && (
    <div
      className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2"
      role="alert"
    >
      <AlertCircle className="h-5 w-5 flex-shrink-0" />
      <span>{error || localError}</span>
    </div>
  );

  const successBanner = successNote && (
    <div
      className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg flex items-center gap-2"
      role="status"
    >
      <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
      <span>{successNote}</span>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <div className="lg:hidden bg-gray-900 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-6 w-6 text-teal-400" />
          <span className="text-lg font-bold text-white">{branding.brandName}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <ShieldCheck className="h-3.5 w-3.5 text-teal-400" />
          <span>Privacy-first</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-gray-50 py-12 px-6 sm:px-8 lg:px-12">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {phase === 'request' ? 'Forgot your password?' : 'Enter your reset code'}
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              {phase === 'request'
                ? `Enter your ${branding.brandName} account email and we will send you a verification code.`
                : 'Check your inbox for a 6-digit verification code, then set a new password.'}
            </p>
          </div>

          {successBanner}
          {errorBanner}

          {phase === 'request' ? (
            <form className="space-y-5" onSubmit={handleRequest}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="you@example.com"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5" />
                    Sending code...
                  </>
                ) : (
                  'Send code'
                )}
              </button>
            </form>
          ) : (
            <form className="space-y-5" onSubmit={handleConfirm}>
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                  Verification code
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.trim())}
                  className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="123456"
                />
              </div>

              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                  New password
                </label>
                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="At least 8 characters"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must include uppercase, lowercase, and a number.
                </p>
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm new password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="Repeat the new password"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5" />
                    Resetting...
                  </>
                ) : (
                  'Reset password'
                )}
              </button>
            </form>
          )}

          <p className="text-center text-sm">
            <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex lg:w-2/5 bg-gray-900 text-gray-100 flex-col items-center justify-center px-12">
        <div className="max-w-sm space-y-8">
          <div className="flex items-center gap-3">
            <Activity className="h-10 w-10 text-teal-400" />
            <span className="text-2xl font-bold text-white">{branding.brandName}</span>
          </div>
          <p className="text-lg text-gray-300 leading-relaxed">
            We'll email a one-time verification code to confirm it's you, then you can choose a new password.
          </p>
          <div className="flex items-center gap-2 border border-gray-700 rounded-full px-4 py-2 text-sm text-gray-300">
            <ShieldCheck className="h-4 w-4 text-teal-400 flex-shrink-0" />
            <span>Codes expire after a short time.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
